import { Icon } from 'app/components/common/Icon';
import { PercentAmpel } from 'app/components/common/PercentAmpel';
import { AgenturNameCell } from 'app/components/shared/AgenturName.cell';
import { Table } from 'app/components/table/table';
import { AggregrationType, TableModel, TableRow } from 'app/components/table/table.model';
import { useStore } from 'app/context';
import { NachfolgePlanungVm } from 'app/stores/ui/nachfolgeplanung.ui.store';
import { formatNum } from 'app/utils';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
import { useHistoryWithQuery } from 'app/hooks/useHistoryWithQuery';
import { useLocation } from 'react-router-dom';
import routes from 'routes';
import { AgtBestandsnachfolgeplanung } from './AgtBestandsnachfolgeplanung';
import Panel from 'app/components/Panel';

const FocusThemnenCol = (row: TableRow<NachfolgePlanungVm>) => {
	return (
		<>
			<div className="tag-list whitespace-wrap">
				{row.data.focusThemen.map((x) => (
					<span className="tag is-small" style={{ backgroundColor: x.def.background, color: x.def.color || 'inherit' }} key={x.def.keyThema}>
						{x.def.themaShort}
					</span>
				))}
			</div>
		</>
	);
};

const NachfolgeplanungSidePanel = observer(({ agtId, onClose }: { agtId: number; onClose: () => void }) => {
	const { nachfolgeplanungUiStore } = useStore();
	const [nachfolgePlanung, setNachfolgePlanung] = useState<NachfolgePlanungVm | null>(null);

	useEffect(() => {
		nachfolgeplanungUiStore.load().then(() => {
			const items = nachfolgeplanungUiStore.items;
			const found = items.find((i) => i.agt.agtId === agtId);
			setNachfolgePlanung(found || null);
		});
	}, [nachfolgeplanungUiStore, agtId]);

	if (!nachfolgePlanung) return null;

	return (
		<Panel classModifier="berichte-panel" title={nachfolgePlanung.agt.name} subtitle={`Agentur ID: ${agtId}`} header={<></>} onClose={onClose}>
			<div className="pad-1rem">
				<div className="content">
					<AgtBestandsnachfolgeplanung agtId={agtId} />
				</div>
			</div>
		</Panel>
	);
});

export const BestandsnachfolgeplanungListe = observer(() => {
	const { nachfolgeplanungUiStore, berichteUiiStore, agenturListUiStore } = useStore();
	const history = useHistoryWithQuery();
	const location = useLocation();
	const [selectedAgtId, setSelectedAgtId] = useState<number | undefined>(undefined);

	const tm = useMemo(() => {
		const getAggProz = (path: string, yellow: number, green: number) => {
			return [
				{
					path,
					format: formatNum,
					aggType: AggregrationType.avg,
					render: (tm: TableModel<NachfolgePlanungVm, number>, aggValue?: number) => {
						if (!aggValue) return <></>;
						return <PercentAmpel value={aggValue} yellowMin={yellow} greenMin={green} />;
					},
				},
			];
		};

		const tm = new TableModel<NachfolgePlanungVm, number>();
		const cols = [
			{
				label: 'Agentur Inhaber',
				path: 'inhaberName',
				render: AgenturNameCell,
			},
			{
				label: 'Alter',
				path: 'inhaberAge',
				agg: [
					{
						path: 'inhaberAge',
						format: (v: number) => Math.round(v).toString(),
						aggType: AggregrationType.avg,
					},
				],
			},
			{
				label: 'Kunden',
				path: 'anzKunden18',
				agg: [
					{
						path: 'anzKunden18',
						format: formatNum,
						aggType: AggregrationType.sum,
					},
				],
			},
			{
				label: ['Gesamt-', 'bewertung VJ'],
				path: 'gesamtbewertungVJ',
				format: formatNum,
				agg: [
					{
						path: 'gesamtbewertungVJ',
						format: formatNum,
						aggType: AggregrationType.sum,
					},
				],
			},
			{
				label: ['Bestand', 'Sach VJ'],
				path: 'bestandSachVJ',
				format: formatNum,
				agg: [
					{
						path: 'bestandSachVJ',
						format: formatNum,
						aggType: AggregrationType.sum,
					},
				],
			},
			{
				label: ['Bestand', 'Digi ID'],
				path: 'prozDigiId18',
				agg: getAggProz('prozDigiId18', 60, 85),
				render: (row: TableRow<NachfolgePlanungVm>) => {
					return <PercentAmpel value={row.data.prozDigiId18} yellowMin={60} greenMin={85} />;
				},
			},
			{
				label: ['Bestand', 'wEWE'],
				path: 'prozWewe18',
				agg: getAggProz('prozWewe18', 35, 60),
				render: (row: TableRow<NachfolgePlanungVm>) => {
					return <PercentAmpel value={row.data.prozWewe18} yellowMin={35} greenMin={60} />;
				},
			},
			{
				label: ['Bestand', 'mAZ'],
				path: 'prozMaz18',
				agg: getAggProz('prozMaz18', 30, 60),
				render: (row: TableRow<NachfolgePlanungVm>) => {
					return <PercentAmpel value={row.data.prozMaz18} yellowMin={30} greenMin={60} />;
				},
			},
			{
				label: ['Fokus', 'Themen'],
				path: 'hasFocusThema',
				render: FocusThemnenCol,
				agg: [
					{
						path: 'hasFocusThema',
						aggType: AggregrationType.count,
					},
				],
			},
		];
		tm.setCols(cols);
		tm.sortBy = 'inhaberAge';
		tm.sortAsc = false;
		tm.idProperty = 'agtId';
		tm.idType = 'number';

		tm.onRowClick = (row: TableRow<NachfolgePlanungVm>) => {
			const p = routes.BERICHTE.getPath('bestand', 'bestandsnachfolge', 'nachfolgeplanung', row.data.agt.agtId.toString());
			history.pushWithQuery(p);
		};
		return tm;
	}, [history, agenturListUiStore.filterHasChanged]);

	// Parse agtId from URL
	useEffect(() => {
		const pathParts = location.pathname.split('/');
		// Expected URL: /berichte/bestand/bestandsnachfolge/nachfolgeplanung/:agtId
		if (pathParts.length >= 6 && pathParts[2] === 'bestand' && pathParts[3] === 'bestandsnachfolge' && pathParts[4] === 'nachfolgeplanung') {
			const agtIdStr = pathParts[5];
			const agtId = parseInt(agtIdStr);
			if (!isNaN(agtId)) {
				setSelectedAgtId(agtId);
			} else {
				setSelectedAgtId(undefined);
			}
		} else {
			setSelectedAgtId(undefined);
		}
	}, [location]);

	const onClose = () => {
		const p = routes.BERICHTE.getPath('bestand', 'bestandsnachfolge', 'nachfolgeplanung');
		history.pushWithQuery(p);
	};

	useEffect(() => {
		nachfolgeplanungUiStore.load().then(() => {
			let res = nachfolgeplanungUiStore.items;
			res = agenturListUiStore.applyFilterOnAgt(res) as NachfolgePlanungVm[];
			if (berichteUiiStore.selectedAgt) {
				const agtId = berichteUiiStore.selectedAgt.agtId;
				res = res.filter((d) => d.agt.agtId === agtId);
			}
			tm.setRowData(res);
		});
	}, [tm, berichteUiiStore.selectedAgt, agenturListUiStore.filterHasChanged, nachfolgeplanungUiStore]);

	return (
		<>
			{selectedAgtId && <NachfolgeplanungSidePanel agtId={selectedAgtId} onClose={onClose} />}
			<div className="table-container">
				<Table tm={tm} stickyHeader={true} stickyFooter={true}></Table>
			</div>
		</>
	);
});
